import React, { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import DatePicker from 'react-datepicker'

import { GET, httpOk } from 'helpers/api'
import { formatMoney, formatDate } from 'helpers/helpers'
import { brandName } from 'helpers/brand'

const QUICK_FILTERS = [
	{ id: 'month', key: 'this_month' },
	{ id: 'prev', key: 'last_month' },
	{ id: 'week', key: 'filter_7days' },
	{ id: '30', key: 'filter_30days' },
]

function getDateRange(id) {
	const now = new Date()
	const fmt = d => formatDate(d, 'yyyy-MM-dd')
	if (id === 'month') return { start: fmt(new Date(now.getFullYear(), now.getMonth(), 1)), end: fmt(now) }
	if (id === 'prev') {
		const s = new Date(now.getFullYear(), now.getMonth() - 1, 1)
		const e = new Date(now.getFullYear(), now.getMonth(), 0)
		return { start: fmt(s), end: fmt(e) }
	}
	if (id === 'week') { const d = new Date(now); d.setDate(d.getDate() - 6); return { start: fmt(d), end: fmt(now) } }
	const d = new Date(now); d.setDate(d.getDate() - 29); return { start: fmt(d), end: fmt(now) }
}

const mln = (v) => {
	v = Number(v) || 0
	const a = Math.abs(v)
	if (a >= 1e9) return (v / 1e9).toFixed(2) + ' mlrd'
	if (a >= 1e6) return (v / 1e6).toFixed(1) + ' mln'
	if (a >= 1e3) return Math.round(v / 1e3) + ' ming'
	return String(Math.round(v))
}

const CAT_COLORS = ['#e5556e', '#f2996a', '#eab308', '#4f79e8', '#22bfa9', '#8b5cf6']

function MonthlyReport() {
	const { t } = useTranslation()
	const [data, setData] = useState(null)
	const [quick, setQuick] = useState('month')
	const [range, setRange] = useState(getDateRange('month'))
	const [custom, setCustom] = useState({ start: null, end: null })

	const load = useCallback(async (r) => {
		const res = await GET('/analytics/monthly-report', { start_date: r.start, end_date: r.end }, { loader: true })
		if (httpOk(res)) setData(res.data)
	}, [])

	useEffect(() => { load(range) }, [range, load])

	function pickQuick(id) { setQuick(id); setCustom({ start: null, end: null }); setRange(getDateRange(id)) }
	function applyCustom(start, end) {
		setCustom({ start, end })
		if (start && end) { setQuick(''); setRange({ start: formatDate(start, 'yyyy-MM-dd'), end: formatDate(end, 'yyyy-MM-dd') }) }
	}

	const d = data
	/* ⚠️ MAXRAJ — `paid + debt`, `total` EMAS (2026-08-13 da tuzatildi).
	   `sales.total` endi NETTO (qaytarilgani ayirilgan), `paid` va `debt` esa
	   BRUTTO qolgan — ular qaytarishga umuman tegmaydi. Nisbat aralashib,
	   paidPct 100 dan oshib ketardi va ekranda «−81% насия» kabi absurd yozuv
	   chiqardi (o'lchandi: sifatmetall 115–123%, test tenantda 181%).
	   Ustiga polosa to'liq «to'langan» rangda qolib, mavjud nasiya
	   ko'rinmasdi (yaroqsiz `width: -81%` brauzer tomonidan tashlanadi).
	   `paid + debt` — ikkalasi bir xil (brutto) asosdan, shuning uchun to'g'ri. */
	const paidBase = d ? (Number(d.sales.paid) || 0) + (Number(d.sales.debt) || 0) : 0
	const paidPct = paidBase > 0
		? Math.min(100, Math.max(0, Math.round((Number(d.sales.paid) || 0) / paidBase * 100)))
		: 0
	const topMax = d ? Math.max(...d.top_categories.map(c => c.amt), 1) : 1

	return (
		<div className="mrep">
			<style>{CSS}</style>

			<div className="m-top">
				<div className="m-brand">
					<div className="m-mark">
						<svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 21V5a2 2 0 0 1 2-2h9l5 5v13" /><path d="M9 8h4M9 12h7M9 16h7" /></svg>
					</div>
					<div>
						<h4 className="m-h1">{t('monthly_report')}</h4>
						{/* Korxona nomi — ilgari "ASProfil" kodga yozib qo'yilgan edi va
						    uni hamma tenant ko'rardi (helpers/brand.js). */}
						<div className="m-sub">{[brandName(), d ? `${formatDate(d.period.start)} — ${formatDate(d.period.end)}` : '...'].filter(Boolean).join(' · ')}</div>
					</div>
				</div>
				<div className="m-tools">
					<div className="m-chips">
						{QUICK_FILTERS.map(f => (
							<button key={f.id} className={'m-chip' + (quick === f.id ? ' on' : '')} onClick={() => pickQuick(f.id)}>{t(f.key)}</button>
						))}
						<span><DatePicker selectsRange startDate={custom.start} endDate={custom.end} dateFormat="dd.MM.yyyy"
							onChange={([s, e]) => applyCustom(s, e)} placeholderText={t('select_date')} className="m-dp" isClearable /></span>
					</div>
					<button className="m-print" onClick={() => window.print()}><i className="uil-print" /> {t('print')}</button>
				</div>
			</div>

			{!d ? <div className="m-empty">{t('loading')}</div> : (
				<>
					{/* Umumiy oqim */}
					<div className="m-flow">
						<div className="m-fcard in">
							<div className="m-flbl">{t('gross_income')}</div>
							<div className="m-fbig">{mln(d.gross_profit)}</div>
							<div className="m-fsub">{t('sales_income')}: {mln(d.sales.total)}</div>
						</div>
						<div className="m-arrow">−</div>
						<div className="m-fcard out">
							<div className="m-flbl">{t('expense_total_flow')}</div>
							<div className="m-fbig">{mln(d.out.total)}</div>
							<div className="m-fsub">{t('flow_out_hint')}</div>
						</div>
						<div className="m-arrow">=</div>
						<div className={'m-fcard res' + (d.net < 0 ? ' neg' : '')}>
							<div className="m-flbl">{t('net_profit')}</div>
							<div className="m-fbig">{mln(d.net)}</div>
							<div className="m-fsub">{t('gross_income')} − {t('total_expense')}</div>
						</div>
					</div>

					<div className="m-grid">
						{/* SAVDO tafsiloti */}
						<div className="m-card">
							<div className="m-hd"><span className="m-ic in">▲</span>{t('sales_detail')}</div>
							<div className="m-rowbig">
								<span>{t('total_sales')}</span><b>{mln(d.sales.total)}</b>
							</div>
							<div className="m-split">
								<div className="m-splitbar">
									<i style={{ width: paidPct + '%', background: 'var(--m-in)' }} />
									<i style={{ width: (100 - paidPct) + '%', background: 'var(--m-warn)' }} />
								</div>
							</div>
							<div className="m-line"><span className="m-dot" style={{ background: 'var(--m-in)' }} />{t('cash_received')}<b>{mln(d.sales.paid)}</b></div>
							<div className="m-line"><span className="m-dot" style={{ background: 'var(--m-warn)' }} />{t('on_credit')}<b className="warn">{mln(d.sales.debt)}</b></div>
							{/* Qaytarilgan tovar — «Умумий савдо» dan ALLAQACHON ayirilgan (backend), bu yerda faqat ko'rsatiladi */}
							{Number(d.sales.returned) > 0 &&
								<div className="m-line"><span className="m-dot" style={{ background: 'var(--m-out)' }} />{t('returned')}<b className="warn">-{mln(d.sales.returned)}</b></div>
							}
								<div className="m-note"><b>{100 - paidPct}%</b> {t('nasiya_hint')}</div>
						</div>

						{/* CHIQIM */}
						<div className="m-card">
							<div className="m-hd"><span className="m-ic out">▼</span>{t('expense_where')}</div>
							<div className="m-line"><span className="m-dot" style={{ background: 'var(--m-out)' }} />{t('operational_expenses')}<b className="out">{mln(d.out.expenses)}</b></div>
							<div className="m-line"><span className="m-dot" style={{ background: '#f2996a' }} />{t('paid_to_suppliers')} <span style={{ color: 'var(--m-fnt)', fontSize: 11, fontWeight: 500 }}>(USD)</span><b className="out">{mln(d.out.suppliers)} $</b></div>
							<div className="m-line"><span className="m-dot" style={{ background: '#8b5cf6' }} />{t('to_employees')}<b className="out">{mln(d.out.employees)}</b></div>
							<div className="m-rowbig" style={{ marginTop: 8 }}><span>{t('total_expense')}</span><b className="out">{mln(d.out.total)}</b></div>
						</div>

						{/* QARZ HOLATI */}
						<div className="m-card">
							<div className="m-hd"><span className="m-ic">⇄</span>{t('debt_status')} <span className="m-r">{t('current_state')}</span></div>
							<div className="m-debt">
								<div className="m-dcard">
									<div className="m-dlbl">{t('clients_owe_us')}</div>
									<div className="m-dval in">{mln(d.debt.receivable)}</div>
									<div className="m-dsub">{t('receivable_debtor')}</div>
								</div>
								<div className="m-dcard">
									<div className="m-dlbl">{t('we_owe_suppliers')}</div>
									<div className="m-dval out">{mln(d.debt.payable)}</div>
									<div className="m-dsub">{t('payable_creditor')}</div>
								</div>
							</div>
						</div>

						{/* KASSA + top moddalar */}
						<div className="m-card">
							<div className="m-hd"><span className="m-ic">▧</span>{t('cash_balance')}</div>
							<div className="m-cash">{mln(d.cash_now)}</div>
							<div className="m-cashsub">{t('cash_note')}</div>
							<div className="m-hd" style={{ margin: '16px 0 10px' }}><span className="m-ic out">▤</span>{t('top_expense_categories')}</div>
							{d.top_categories.map((c, i) => (
								<div className="m-cat" key={i}>
									<span className="m-catn">{c.name}</span>
									<div className="m-cattrack"><i style={{ width: Math.max(3, c.amt / topMax * 100) + '%', background: CAT_COLORS[i % CAT_COLORS.length] }} /></div>
									<span className="m-catv">{mln(c.amt)}</span>
								</div>
							))}
						</div>
					</div>

					<div className="m-foot">
						⚠️ {t('opening_balance_note')}
					</div>
				</>
			)}
		</div>
	)
}

const CSS = `
.mrep{--m-in:#1fae74;--m-out:#e5556e;--m-warn:#f2996a;--m-blue:#4f79e8;--m-ink:#1c2a38;--m-mut:#6b7c8d;--m-fnt:#9aa9b8;--m-line:#e6ecf3;--m-line2:#eef2f7;--m-ins:#f2f5fa;--m-pan:#fff;font-size:14px;color:var(--m-ink)}
.m-top{display:flex;align-items:flex-end;justify-content:space-between;gap:16px;flex-wrap:wrap;margin-bottom:18px}
.m-brand{display:flex;align-items:center;gap:12px}
.m-mark{width:40px;height:40px;border-radius:10px;background:linear-gradient(150deg,var(--m-blue),#22bfa9);display:grid;place-items:center;flex:none}
.m-mark svg{width:22px;height:22px}
.m-h1{margin:0;font-size:19px;font-weight:800;letter-spacing:-.2px}
.m-sub{color:var(--m-mut);font-size:12.5px;font-weight:500;margin-top:1px}
.m-tools{display:flex;gap:8px;align-items:center;flex-wrap:wrap}
.m-chips{display:flex;gap:5px;align-items:center;flex-wrap:wrap;background:var(--m-pan);padding:5px;border-radius:11px;border:1px solid var(--m-line)}
.m-chip{border:0;background:transparent;color:var(--m-ink);font:inherit;font-size:12.5px;font-weight:600;padding:7px 12px;border-radius:8px;cursor:pointer}
.m-chip.on{background:var(--m-blue);color:#fff}
.m-dp{border:1px solid var(--m-line);border-radius:8px;padding:7px 10px;font-size:12.5px;width:130px}
.m-print{display:inline-flex;align-items:center;gap:6px;border:1px solid var(--m-line);background:var(--m-pan);color:var(--m-blue);font-weight:700;font-size:12.5px;padding:8px 13px;border-radius:10px;cursor:pointer}
.m-empty{padding:40px;text-align:center;color:var(--m-mut)}
.m-flow{display:flex;align-items:stretch;gap:10px;margin-bottom:14px;flex-wrap:wrap}
.m-fcard{flex:1;min-width:180px;background:var(--m-pan);border:1px solid var(--m-line);border-radius:14px;padding:16px 18px;box-shadow:0 1px 2px rgba(16,34,46,.04),0 5px 18px rgba(16,34,46,.05);position:relative;overflow:hidden}
.m-fcard::before{content:"";position:absolute;left:0;top:0;bottom:0;width:5px}
.m-fcard.in::before{background:var(--m-in)}.m-fcard.out::before{background:var(--m-out)}.m-fcard.res::before{background:var(--m-blue)}.m-fcard.res.neg::before{background:var(--m-out)}
.m-flbl{font-size:11px;letter-spacing:.06em;text-transform:uppercase;color:var(--m-mut);font-weight:700}
.m-fbig{font-size:27px;font-weight:820;letter-spacing:-.6px;margin:8px 0 3px;font-variant-numeric:tabular-nums}
.m-fcard.in .m-fbig{color:var(--m-in)}.m-fcard.out .m-fbig{color:var(--m-out)}.m-fcard.res .m-fbig{color:var(--m-blue)}.m-fcard.res.neg .m-fbig{color:var(--m-out)}
.m-fsub{font-size:11.5px;color:var(--m-fnt);font-weight:500}
.m-arrow{display:flex;align-items:center;font-size:24px;font-weight:800;color:var(--m-fnt)}
@media(max-width:720px){.m-arrow{display:none}.m-fcard{min-width:140px}}
.m-grid{display:grid;grid-template-columns:1fr 1fr;gap:13px}
@media(max-width:820px){.m-grid{grid-template-columns:1fr}}
.m-card{background:var(--m-pan);border:1px solid var(--m-line);border-radius:14px;padding:16px 17px;box-shadow:0 1px 2px rgba(16,34,46,.04),0 4px 16px rgba(16,34,46,.04)}
.m-hd{display:flex;align-items:center;gap:8px;margin-bottom:14px;font-size:13.5px;font-weight:750}
.m-ic{color:var(--m-blue)}.m-ic.in{color:var(--m-in)}.m-ic.out{color:var(--m-out)}
.m-r{margin-left:auto;font-size:11px;color:var(--m-fnt);font-weight:600}
.m-rowbig{display:flex;justify-content:space-between;align-items:baseline;font-size:14px;font-weight:600;padding:4px 0}
.m-rowbig b{font-size:18px;font-weight:820;font-variant-numeric:tabular-nums}
.m-split{margin:10px 0}
.m-splitbar{display:flex;height:12px;border-radius:7px;overflow:hidden;background:var(--m-ins)}
.m-splitbar>i{height:100%}
.m-line{display:flex;align-items:center;gap:9px;padding:9px 0;border-bottom:1px solid var(--m-line2);font-size:13px;font-weight:600}
.m-line:last-of-type{border-bottom:0}
.m-line b{margin-left:auto;font-weight:800;font-variant-numeric:tabular-nums}
.m-dot{width:10px;height:10px;border-radius:3px;flex:none}
.m-line b.warn,.warn{color:var(--m-warn)}.m-line b.out,.out{color:var(--m-out)}.in{color:var(--m-in)}
.m-note{margin-top:11px;font-size:12px;color:var(--m-mut);font-weight:500;line-height:1.5;background:var(--m-ins);padding:9px 11px;border-radius:9px}
.m-note b{color:var(--m-warn)}
.m-debt{display:flex;gap:12px}
.m-dcard{flex:1;background:var(--m-ins);border:1px solid var(--m-line);border-radius:11px;padding:14px}
.m-dlbl{font-size:11.5px;color:var(--m-mut);font-weight:600}
.m-dval{font-size:22px;font-weight:820;letter-spacing:-.4px;margin:6px 0 2px;font-variant-numeric:tabular-nums}
.m-dsub{font-size:11px;color:var(--m-fnt);font-weight:500}
.m-cash{font-size:30px;font-weight:820;letter-spacing:-.6px;color:var(--m-blue);font-variant-numeric:tabular-nums}
.m-cashsub{font-size:12px;color:var(--m-fnt);font-weight:500;margin-top:3px}
.m-cat{display:flex;align-items:center;gap:10px;padding:6px 0}
.m-catn{font-size:12.5px;font-weight:650;width:150px;flex:none;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.m-cattrack{flex:1;height:8px;border-radius:5px;background:var(--m-ins);overflow:hidden}
.m-cattrack>i{display:block;height:100%;border-radius:5px}
.m-catv{font-size:12.5px;font-weight:800;font-variant-numeric:tabular-nums;white-space:nowrap}
.m-foot{margin-top:18px;font-size:12px;color:var(--m-fnt);text-align:center;line-height:1.6}
@media print{.m-top .m-tools{display:none}.mrep{color:#000}}
`

export default MonthlyReport
